/**
 * A fixture of the deeper search in the WIRE shape of `GET /cases/{id}` (round 2,
 * 13 Oct 2026). LOCAL DEV ONLY, like the rest of `@/lib/mock`.
 *
 * Everything is invented (companies, people, accounts and item numbers with the 099
 * pattern, the quotation number, the prices):
 *   - `RFQ-INV-PROPOSAL` an inquiry that names one of our quotations. The deeper search
 *                        proposes the account of that quotation (it holds), a candidate
 *                        item with a doubt for the second line, and an alternative for the
 *                        third. The first line carries provisional values for the
 *                        suggested account. "Look again" is offered.
 *
 * Kept in the wire shape so it crosses the same mapper as live data (`@/lib/data`). The
 * builder is a function, so nothing runs at module load and a production build drops it.
 */

import type { LiveQuoteCaseDetail } from "@/lib/api";

const UK_MAILBOX = "sales.example-uk@norican.com";

/** The line keys of the fixture, shared with its proposals. */
export const INVESTIGATION_LINE_KEYS = {
  priced: "d1b2c3d4e5f6a7b8c9e0",
  described: "d1b2c3d4e5f6a7b8c9e1",
  replaced: "d1b2c3d4e5f6a7b8c9e2",
} as const;

function proposalCase(): LiveQuoteCaseDetail {
  return {
    id: "RFQ-INV-PROPOSAL",
    ref: "RFQ-INV-PROPOSAL",
    customer: "Harrow Castings Ltd",
    area: "uk",
    intent: "rfq_quote",
    line_count: 3,
    confidence: 0,
    exceptions: [],
    assignee: null,
    age_minutes: 42,
    state: "needs-review",
    received_at: "2026-10-12T08:15:00Z",
    subject: "Your quotation SQ0990811: two more items",
    sender_name: "Dana Whitfield",
    sender_email: "dana.whitfield@harrow-castings.example",
    mailbox: UK_MAILBOX,
    route: "ask-customer",
    is_rfq: true,
    origin: "customer",
    lines_auto: 0,
    review_reason: "Customer suggested, please confirm",
    reply_found: false,
    work_status: "open",
    work: {
      status: "open",
      status_by: null,
      status_at: null,
      status_note: null,
      reopened_at: null,
      assignee: null,
      assigned_at: null,
      customer_account: null,
      customer_by: null,
      customer_at: null,
      version: 0,
    },
    fields: [],
    lines: [
      {
        enquiry_line: "4 x 0990021 wear plate",
        qty: 4,
        item: "0990021",
        auto_accept: false,
        outcome: "match",
        why: "The item number 0990021 is in the mail and is in the catalogue.",
        status: "suggested",
        item_name: "WEAR PLATE 12 MM",
        catalogue_area: "uk",
        is_spare: true,
        match_basis: "exact_number",
        reason_code: "customer_open",
        line_key: INVESTIGATION_LINE_KEYS.priced,
        decision: null,
        unit: "pcs",
        price_status: "no_customer",
        lead_time_days: null,
        provisional: {
          account: "0990301",
          unit_price: "41.50",
          currency: "GBP",
          price_unit_qty: 1,
          uom: "pcs",
          price_group: "UK-LIST",
          valid_to: "2026-12-31",
          shared_by_candidates: false,
          qty_assumed: null,
          discount_pct: "5.0",
          discount_basis: "account_usual",
          last_unit_price: "39.00",
          last_quoted_on: "2026-06-02",
          lead_days: 21,
          lead_source: "item_master_sales",
        },
      },
      {
        enquiry_line: "1 blade for the wheel, as last time",
        qty: 1,
        item: null,
        auto_accept: false,
        outcome: "abstain",
        why: "No item number is written for this line.",
        status: "open",
        line_key: INVESTIGATION_LINE_KEYS.described,
        decision: null,
        price_status: "no_item",
      },
      {
        enquiry_line: "2 x 0990031 seal kit",
        qty: 2,
        item: "0990031",
        auto_accept: false,
        outcome: "match",
        why: "The item number 0990031 is in the mail and is in the catalogue.",
        status: "suggested",
        item_name: "SEAL KIT (use 0990032)",
        catalogue_area: "uk",
        is_spare: true,
        match_basis: "exact_number",
        reason_code: "customer_open",
        line_key: INVESTIGATION_LINE_KEYS.replaced,
        decision: null,
        price_status: "no_customer",
      },
    ],
    lines_extracted: true,
    why_here: ["The customer is suggested and needs confirming."],
    preflight: [],
    customer_resolution: {
      account: "0990301",
      confident: false,
      how: "quote-ref",
      via: "body",
      entity: "uk",
      name: "Harrow Castings Ltd",
      company: "Harrow Castings",
      role: "customer",
      suggested: true,
      basis: "Suggested by the deeper search: the account of our quotation SQ0990811.",
      forwarded_from: null,
      portal: null,
      candidates: [
        {
          account: "0990301",
          name: "Harrow Castings Ltd",
          kind: "investigation",
          basis: "The account of our quotation SQ0990811.",
          city: "Harrow",
          postcode: "HA1",
          score: null,
        },
        {
          account: "0990302",
          name: "Harrow Castings (Northern) Ltd",
          kind: "investigation",
          basis:
            "The account of the sender. Quoted on this account so far: 2 quotations in 12 months.",
          city: "Leeds",
          postcode: "LS9",
          score: null,
        },
      ],
      confirmed: false,
    },
    matching_review: null,
    investigation: {
      status: "complete",
      on_demand: false,
      pending: false,
      look_again_allowed: true,
      customer: {
        proposal_id: "a1f09c2e77b0",
        kind: "customer",
        line_key: null,
        account: "0990301",
        item: null,
        name: "Harrow Castings Ltd",
        status: "proposed",
        role: "pick",
        reason: "Our quotation SQ0990811 is for this account.",
        doubt: null,
        question: null,
        signals: ["document"],
        evidence: [
          {
            lookup: "account_of_document",
            query: "SQ0990811",
            rows: ["Quotation SQ0990811 of 02 Jun 2026, account 0990301"],
            decided: true,
          },
          {
            lookup: "account_details",
            query: "0990301",
            rows: ["Harrow Castings Ltd, Harrow HA1, price group UK-LIST"],
            decided: false,
          },
        ],
        decision: null,
      },
      other_account: {
        proposal_id: "a1f09c2e77b9",
        kind: "customer",
        line_key: null,
        account: "0990302",
        item: null,
        name: "Harrow Castings (Northern) Ltd",
        status: "proposed",
        role: "alternative",
        reason:
          "The account of the sender. Quoted on this account so far: 2 quotations in 12 months.",
        doubt: null,
        question: null,
        signals: ["history"],
        evidence: [
          {
            lookup: "account_activity",
            query: "0990301; 0990302",
            rows: ["0990302: 2 quotations in 12 months"],
            decided: false,
          },
        ],
        decision: null,
      },
      lines: [
        {
          proposal_id: "a1f09c2e77b1",
          kind: "item",
          line_key: INVESTIGATION_LINE_KEYS.described,
          account: null,
          item: "0990022",
          name: "BLADE 180 MM",
          status: "downgraded",
          role: "pick",
          reason: "The customer was quoted this item on SQ0990811.",
          doubt: "Only the description supports this item. Check the size.",
          question: "Which blade size does the customer need?",
          signals: [],
          evidence: [
            {
              lookup: "quotation_lines",
              query: "SQ0990811",
              rows: ["Line 3: 0990022 BLADE 180 MM, quantity 1"],
              decided: true,
            },
          ],
          decision: null,
        },
        {
          proposal_id: "a1f09c2e77b2",
          kind: "item",
          line_key: INVESTIGATION_LINE_KEYS.replaced,
          account: null,
          item: "0990032",
          name: "SEAL KIT MK2",
          status: "proposed",
          role: "alternative",
          reason: "The written item is replaced by this item in F&O.",
          doubt: null,
          question: null,
          signals: [],
          evidence: [
            {
              lookup: "replacement_chain",
              query: "0990031",
              rows: ["0990031 is replaced by 0990032 SEAL KIT MK2"],
              decided: true,
            },
          ],
          decision: null,
        },
      ],
      questions: ["Which blade size does the customer need?"],
      provisional_account: "0990301",
      provisional_basis: "proposal",
      revision: null,
    },
  };
}

export const INVESTIGATION_WIRE_CASES: Record<string, () => LiveQuoteCaseDetail> = {
  "RFQ-INV-PROPOSAL": proposalCase,
};
